import React from "react";
import { Link } from "react-router-dom";

// Privacy policy (/privacy) and terms (/terms). Public pages: the App Store
// and Google Play listings link to them, so keep these paths stable.
// Update LAST_UPDATED whenever the wording changes.
const OWNER = "Jack Emery";
const CONTACT_EMAIL = "info@playjangle.com";
const COUNTRY = "Australia";
const LAST_UPDATED = "17 October 2026";

function Page({ title, testId, children }) {
  return (
    <div className="min-h-screen" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-3">Legal</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mb-3">{title}</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated {LAST_UPDATED}</p>
        <div className="space-y-4 text-base leading-relaxed max-w-prose [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:pt-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:underline [&_a]:underline-offset-4">
          {children}
        </div>
      </div>
    </div>
  );
}

const Mail = () => <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

export function Privacy() {
  return (
    <Page title="Privacy policy" testId="privacy-page">
      <p>
        Jangle is a word game at playjangle.com and in the Jangle apps for Android and iPhone. It is run by {OWNER}
        {" "}in {COUNTRY} ("we", "us"). This policy explains what we collect when you play, why, and what you can do
        about it. Questions go to <Mail />.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Your account:</strong> your email address, username and password. The password is stored only as a
          secure hash, never as plain text.
        </li>
        <li>
          <strong>Your profile:</strong> the raider you pick, a profile picture if you uploaded one before photos were removed, your Elo rating,
          your wins, losses and draws, and when you joined.
        </li>
        <li>
          <strong>Your games:</strong> every move, the tiles in your hand, the board and the result, for games against
          people and against bots. Daily puzzle attempts and results are saved too.
        </li>
        <li>
          <strong>Messages:</strong> chat messages you send during a game, friend requests, and messages you send to
          friends.
        </li>
        <li>
          <strong>Feedback:</strong> anything you send with "Send feedback", including an optional screenshot, the page
          you were on and basic device details (browser or app, screen size).
        </li>
        <li>
          <strong>Notifications:</strong> if you allow notifications in the app, a push token from Apple or Google so we
          can tell you when it is your turn or a friend messages you.
        </li>
        <li>
          <strong>Activity:</strong> the last time you were active and which days you played, so we can count active
          players.
        </li>
      </ul>

      <h2>How we use it</h2>
      <ul>
        <li>To run your account and sign you in, and to email you a link if you forget your password.</li>
        <li>To match you with opponents, run your games, keep your rating and show your history.</li>
        <li>To deliver chat, friend messages and notifications.</li>
        <li>
          To improve Jangle. Finished games are kept to tune our bots, balance ratings and design puzzles. Bots do not
          learn from you while you play.
        </li>
        <li>To read your feedback, fix bugs, and stop cheating, abuse and spam.</li>
      </ul>
      <p>We do not sell your information, and we do not show ads.</p>

      <h2>What other players can see</h2>
      <p>
        Other players can see your username, raider, rating, game record and your games, including finished games on the Watch page. Chat in a game is seen by both players.
        Messages to a friend are seen only by you and that friend. Your email address is never shown to other players.
      </p>

      <h2>Who we share it with</h2>
      <p>We use a few services to run Jangle. They handle your information only to provide their service to us:</p>
      <ul>
        <li><strong>Vercel</strong> hosts the website.</li>
        <li><strong>Railway</strong> runs our game servers.</li>
        <li><strong>MongoDB</strong> stores our database.</li>
        <li><strong>Resend</strong> sends password reset emails.</li>
        <li><strong>Google Firebase</strong> and <strong>Apple</strong> deliver push notifications.</li>
      </ul>
      <p>
        These services may store information outside your country, including in the United States. We may also share
        information if the law requires it, or to protect players from harm.
      </p>

      <h2>Cookies and storage</h2>
      <p>
        We use cookies to keep you signed in, and your browser's local storage to remember settings such as dark mode
        and how you arranged your tiles. We do not use advertising cookies.
      </p>

      <h2>How long we keep it</h2>
      <p>
        We keep your account until you delete it. Password reset links expire after one hour. When you delete your
        account we remove your profile, picture, friends, friend messages, game chat, puzzle results, feedback and
        notification tokens, and any game you are playing ends. Finished games stay so your opponents keep their
        records and ratings, but they show "Deleted player" instead of your name. Copies may remain in our database
        backups for a short time before they are overwritten.
      </p>

      <h2>Your choices and rights</h2>
      <ul>
        <li>You can change your username and raider at any time from your profile.</li>
        <li>You can turn notifications off in your phone's settings.</li>
        <li>
          You can delete your account at any time from the bottom of your profile page, in the app or on the website.
        </li>
        <li>
          You can ask us for a copy of your information, or to correct or delete it, by emailing <Mail /> from the
          address on your account.
        </li>
        <li>
          Depending on where you live, you may have other rights, such as objecting to how we use your information or
          complaining to your data protection authority (in Australia, the Office of the Australian Information Commissioner).
        </li>
      </ul>

      <h2>Children</h2>
      <p>
        Jangle is not meant for children under 13, and we do not knowingly collect information from them. If you think a
        child under 13 has an account, email <Mail /> and we will delete it.
      </p>

      <h2>Security</h2>
      <p>
        We protect your information with encrypted connections, hashed passwords and limited access. No system is
        perfectly secure, so please use a password you do not use anywhere else.
      </p>

      <h2>Changes</h2>
      <p>
        If we change this policy we will update the date at the top, and tell you in the game if the change is
        important.
      </p>

      <p className="pt-6 text-sm text-muted-foreground">
        See also our <Link to="/terms">terms and conditions</Link>.
      </p>
    </Page>
  );
}

export function Terms() {
  return (
    <Page title="Terms and conditions" testId="terms-page">
      <p>
        These terms apply when you play Jangle at playjangle.com or in the Jangle apps. Jangle is run by {OWNER} in
        {" "}{COUNTRY} ("we", "us"). By creating an account or playing, you agree to them. If you do not agree, please
        do not use Jangle. Questions go to <Mail />.
      </p>

      <h2>Your account</h2>
      <ul>
        <li>You must be at least 13 years old to make an account.</li>
        <li>Give a real email address you can read, so you can reset your password.</li>
        <li>One account per person. Keep your password to yourself; you are responsible for what happens on your account.</li>
        <li>Pick a username that is not offensive and does not pretend to be someone else. We may change or remove names that break this.</li>
      </ul>

      <h2>Playing fair</h2>
      <p>To keep the game fun for everyone, do not:</p>
      <ul>
        <li>use bots, solvers, word finders or anyone else's help to play your moves in rated games;</li>
        <li>play against your own second account, or arrange results with another player, to change ratings;</li>
        <li>exploit bugs on purpose (please report them with "Send feedback" instead);</li>
        <li>try to break into, overload or disrupt Jangle or other players' accounts.</li>
      </ul>

      <h2>Being kind</h2>
      <p>
        Chat, friend messages and usernames must not be abusive, hateful, sexual, threatening, or
        share someone else's private information. Do not spam or advertise. You are responsible for what you post, and
        you let us store and show it to the people it is meant for so Jangle can work.
      </p>

      <h2>What we can do</h2>
      <p>
        If you break these terms we may remove content, reset ratings, cancel game results, or suspend or close your
        account. Where we can, we will tell you why.
      </p>

      <h2>The game</h2>
      <ul>
        <li>Jangle is free to play. If we ever add paid features, we will explain their terms first.</li>
        <li>
          We keep improving Jangle, so rules, boards, ratings and features can change, and we may remove features or
          reset ratings when needed.
        </li>
        <li>
          Jangle, its name, artwork, raiders, boards and code belong to {OWNER}. You may not copy them or make your own
          version of the game from them.
        </li>
      </ul>

      <h2>No guarantees</h2>
      <p>
        We work to keep Jangle running smoothly, but it is provided "as is". We cannot promise it will always be
        available or free of bugs, or that games and ratings will never be lost. As far as the law allows, we are not
        liable for indirect losses, or for losses from using or not being able to use Jangle. Nothing in these terms
        limits rights you have by law that cannot be limited.
      </p>

      <h2>Leaving</h2>
      <p>
        You can stop playing whenever you like, and delete your account from your profile page at any time. What happens to
        your information then is in our <Link to="/privacy">privacy policy</Link>.
      </p>

      <h2>Changes and law</h2>
      <p>
        We may update these terms. If a change is important we will tell you in the game, and if you keep playing
        after it applies you accept the new terms. These terms are governed by the laws of {COUNTRY}.
      </p>
    </Page>
  );
}
